// freestyle.js — shared parser + canvas state snapshot for the freestyler pipeline.
//
// parseFreestyleSource:
//   Tries to import parseJSObject from @symbo.ls/freestyler. If the export is
//   unavailable (parallel smbls agent hasn't shipped B1 yet, or the dist is stale),
//   falls back to an inline equivalent with identical behaviour: strip markdown fences,
//   locate ({...}) or {...}, normalise to a JS expression, eval via new Function.
//
// canvasStateSnapshot:
//   Returns a minimal, JSON-safe snapshot of the current canvas state for the agent
//   to read via get_canvas_state. Includes tile keys/types, registered component names,
//   and a design-system summary (token set names only — not the raw values).

// --- parseFreestyleSource ---------------------------------------------------
//
// Previously this attempted a dynamic `import('@symbo.ls/freestyler')` to pick
// up the canonical parser when available. That sibling package isn't published
// to npm yet so it's absent from CI's node_modules — Parcel's bundler then
// fails to statically resolve the import even though it's wrapped in
// try/catch, breaking `bun run build` on every PR (a `Failed to resolve
// '@symbo.ls/freestyler'` error). And once the import is "hidden" via string
// indirection, Parcel's bundleInfoMap chokes on the unresolvable specifier
// with `bundleInfoMap[bundleId] is not iterable`.
//
// The inline `parseJSObjectFallback` below is functionally identical to
// freestyler's parseJSObject (same fence-stripping, same eval-via-Function),
// so just use it directly and drop the dynamic-import dance entirely. When
// freestyler ships to npm we can swap back to a normal static import.

import { deepDestringifyFunctions } from 'smbls'

// Normalise raw model output to a bare JS expression: strip markdown fences and
// trailing commas. Extracted so the JSON-authored detection below tests the
// EXACT text the eval sees (a fenced ```json block is still JSON-authored).
//
// The language tag is matched as `[a-z]*`, NOT as `(?:javascript|js|json)?`.
// That alternation is ordered, so on a ```json fence it matched `js` first and
// left a stray `on` glued to the front of the source — every ```json-fenced
// source then died in eval with "on is not defined" and the whole view failed
// to render. Anchored right after the backticks with the object literal
// following, `[a-z]*` cannot over-consume.
function normalizeSourceText(raw) {
  return (
    (raw || '')
      .trim()
      .replace(/^```[a-z]*[ \t]*\r?\n?/i, '')
      .replace(/\s*```\s*$/m, '')
      .trim()
      // Remove trailing commas before closing braces/brackets (common AI output).
      // Safe on arrow bodies: only a comma IMMEDIATELY before `}`/`]` is dropped.
      .replace(/,(\s*[}\]])/g, '$1')
  )
}

// Inline fallback — mirrors freestyler parseJSObject behaviour.
// Accepts arrow-function handlers intact (eval, not JSON.parse).
//
// CRITICAL — do NOT extract the object by scanning for the first `({` in the
// text. A concise arrow object-return `(t) => ({ ... })` CONTAINS `({`, so the
// old `indexOf('({')` heuristic sliced out the arrow's inner object and eval'd
// it standalone, throwing "t is not defined". That single line silently broke
// EVERY freestyle source using the standard row-mapping idiom
// `children: (el, s) => rows.map((r) => ({ key: r.id, … }))` — the drawn-answer
// views, canvas tile injects, and create_extension alike. The whole source is
// a JS object literal; only unwrap an OUTER `({…})` the model added around the
// whole thing, then eval it AS-IS (the arrows stay functions, never evaluated).
// (freestyler's own parseJSObject likely carries the same bug — see
// tickets/smbls.md.)
// The EXACT expression text the parser evaluates (`return <expr>`), derived
// once so an audit hook and the eval can never see different code.
function freestyleExpressionText(raw) {
  let text = normalizeSourceText(raw)
  if (text.startsWith('({') && text.endsWith('})')) {
    // Already a parenthesized object expression — eval verbatim.
    return text
  }
  // Bare `{ … }` — wrap the whole object (first `{` to last `}`) in parens so it
  // parses as an expression, not a block. Inner `({…})` arrow returns are left
  // untouched because we never slice mid-body.
  const braceStart = text.indexOf('{')
  const braceEnd = text.lastIndexOf('}')
  if (braceStart >= 0 && braceEnd > braceStart) {
    text = text.slice(braceStart, braceEnd + 1)
  }
  return '(' + text + ')'
}

// `audit(expressionText)` (REQUIRED) runs on exactly the text about to be
// evaluated and throws to refuse it — the generated-app containment gate
// (workspace functions/generatedAppContainment.js). The audit sees the WHOLE
// text, so string-held handlers of a JSON-authored source (destringified and
// evaluated below) are audited as part of it.
function auditedExpressionText(raw, audit) {
  const expr = freestyleExpressionText(raw)
  // REQUIRED (core-wl v2): no audit, no evaluation. A caller that forgets the
  // hook gets the containment refusal, never an unaudited eval.
  if (typeof audit !== 'function') {
    const err = new Error('parseFreestyleSource: an audit hook is required')
    err.code = 'generated_app_blocked'
    err.violations = [{ rule: 'audit-unavailable', at: null, detail: 'no audit hook' }]
    throw err
  }
  audit(expr)
  return expr
}

function parseJSObjectFallback(raw, audit) {
  return new Function('return ' + auditedExpressionText(raw, audit))()
}

// A source is JSON-AUTHORED when the whole text parses as strict JSON. JSON has
// no function type, so every DOMQL factory in such a source arrived as a QUOTED
// STRING (`"text": "(el, s) => …"`). `new Function('return (…)')` happily evals
// JSON — it IS valid JS — so the def comes back structurally correct but with
// every factory still a string: DOMQL then renders `text` as the literal source
// text on screen and never fires `onInit`/`children` (verified live 2026-07-27
// on /w/natali — "what's our total stock value?" drew a pane whose value read
// `(el, s) => (s.stockTotal || 0).toLocaleString() + ' GEL'` and whose breakdown
// stayed empty because its onInit loader never ran).
//
// Tool-call arguments ARE JSON, so an AI-authored `source` round-tripping as
// JSON is a first-class input shape, not a malformed one — it needs the same
// revive every other JSON-borne DOMQL payload in the platform already gets
// (project data, workspace modules, InsertPanelLibrary, loadProjectData all
// call deepDestringifyFunctions). This parser was the one path that skipped it.
//
// Gated on JSON-parseability rather than on per-value shape ON PURPOSE: a
// healthy JS-object-literal source is left completely untouched, so a data
// string that merely happens to read like an arrow (`'Draft => Review'`) can
// never be eval'd into a function on the normal path.
function isJsonAuthored(text) {
  if (!text || (text[0] !== '{' && text[0] !== '[')) return false
  try {
    JSON.parse(text)
    return true
  } catch (_) {
    return false
  }
}

// Synchronous entry-point — always uses the inline fallback now. See the
// header comment above for why the freestyler dynamic-import dance is gone.
export function parseFreestyleSource(source, opts) {
  const audit = opts && typeof opts.audit === 'function' ? opts.audit : null
  const def = parseJSObjectFallback(source, audit)
  if (!def || typeof def !== 'object') return def
  if (!isJsonAuthored(normalizeSourceText(source))) return def
  // deepDestringifyFunctions writes into the container it's handed and only
  // touches strings that read like a function (smbls `hasFunction`), leaving a
  // failed eval as the original string with a console warning.
  return deepDestringifyFunctions(def, Array.isArray(def) ? [] : {})
}

// The SANDBOX path (ISOLATED-RUNTIME-PLAN §8.1): the same required audit on the
// same exact text, and NO evaluation in this realm. Returns the audited
// expression text — the frame evaluates exactly it — and whether the source
// is JSON-authored (the frame then revives its string-held handlers, as
// parseFreestyleSource does here).
export function auditFreestyleSource(source, opts) {
  const audit = opts && typeof opts.audit === 'function' ? opts.audit : null
  const expr = auditedExpressionText(source, audit)
  return { expr, jsonAuthored: isJsonAuthored(normalizeSourceText(source)) }
}

// --- canvasStateSnapshot ----------------------------------------------------

export function canvasStateSnapshot() {
  // DOMQL function — `this` is the element when called via el.call.
  const el = this
  // ctx lives on the element (or root element's context); canvas state lives on
  // root STATE — use getCanvasV2 so existing tiles are read correctly.
  const ctx = el.context || (el.getRoot && el.getRoot().context) || {}
  const v2 = (el.call && el.call('getCanvasV2')) || {}
  const items = v2.items || {}
  const ds = ctx.designSystem || {}

  // Tiles: just the key, pageKey, and position — no internals.
  // pageKey is what embedComponent uses to look up the component in
  // root.data.components or root.data.pages — report it so the agent
  // can reference the correct key when composing follow-up calls.
  const tiles = Object.entries(items).map(([key, it]) => ({
    key,
    pageKey: it.pageKey || key,
    x: it.x,
    y: it.y,
    w: it.w,
    h: it.h
  }))

  // Report the keys from root STATE data.components — the project-data registry
  // that the tile iframe renderer actually reads via embedComponent. This is the
  // reactive state object (not el.getRoot().data which is the element's .data
  // property — a different object). injectOnCanvas writes here via root.update().
  const rootState =
    el.getRootState?.() || el.state?.root || (el.getRoot && el.getRoot()?.state) || {}
  const dataComponents = Object.keys((rootState.data || {}).components || {})
  // Also include shell-context components for completeness (e.g. framework built-ins
  // the agent might reference), deduped with data registry entries.
  const ctxComponents = Object.keys(ctx.components || {})
  const componentNames = [...new Set([...dataComponents, ...ctxComponents])]

  // Design-system summary: top-level token group names only.
  const dsKeys = Object.keys(ds).filter((k) => k !== 'icons' && k !== 'components')

  // --- Reusable component registry (for freestyle agent) --------------------
  // Full live registry: framework built-ins + shared libraries + brand library
  // + project components — everything the agent can reference by PascalCase key.
  const allCtxComponents = Object.keys(ctx.components || {})
  const REUSABLE_CAP = 250
  const reusableComponents = allCtxComponents.slice(0, REUSABLE_CAP)
  const reusableComponentsTruncated =
    allCtxComponents.length > REUSABLE_CAP ? allCtxComponents.length : undefined

  // --- Design-system token-group key summary (for freestyle agent) ----------
  // For each well-known group, return just the key names (not values) so the
  // agent knows which tokens exist without receiving a potentially huge object.
  const DS_GROUPS = ['color', 'colors', 'typography', 'spacing', 'theme', 'themes', 'icons']
  const DS_GROUP_CAP = 60
  const designSystem = {}
  for (const group of DS_GROUPS) {
    const groupVal = ds[group]
    if (!groupVal || typeof groupVal !== 'object') continue
    const keys = Object.keys(groupVal)
    if (keys.length === 0) continue
    designSystem[group] = keys.slice(0, DS_GROUP_CAP)
    if (keys.length > DS_GROUP_CAP) {
      designSystem[group + 'Truncated'] = keys.length
    }
  }

  return {
    // Back-compat fields
    tiles,
    componentNames,
    // Separate field so the agent can distinguish renderable data-registry
    // components from shell-only context entries.
    dataComponentNames: dataComponents,
    designSystemKeys: dsKeys,
    tileCount: tiles.length,
    // New: full reusable registry — agent MUST check these before building new markup.
    reusableComponents,
    ...(reusableComponentsTruncated !== undefined && { reusableComponentsTruncated }),
    // New: token-group key summary — agent MUST use these DS tokens, not raw values.
    designSystem: Object.keys(designSystem).length ? designSystem : undefined
  }
}
